

//-------------------------------------------

import React, { useState, useEffect } from "react";
import { FontAwesomeIcon  } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faImage,
  faCheck,
  faTimes,
  faTrash,
  faChevronDown,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { addQuestions, uploadFile, getDetails } from "../../services/quiz";
import toast from "react-hot-toast";
import { generateConfirm } from "../../helpers";

const AddQuestion = () => {
  const navigate = useNavigate();

  const location = useLocation();

  // Get quiz_id from URL params and localStorage
  const { quizId: urlQuizId } = useParams();
  const storedQuizId = localStorage.getItem("current_quiz_id");
  const quiz_id = urlQuizId || storedQuizId || "";

  // Track deleted questions
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);

  const [questions, setQuestions] = useState([
    {
      question_id: "", // Will be empty for new questions
      question: "",
      questionImage: null, // Local state for preview
      question_file: "", // URL for API
      options: [
        { text: "", image: null, isCorrect: false, type: "text", option: "" },
        { text: "", image: null, isCorrect: false, type: "text", option: "" },
        { text: "", image: null, isCorrect: false, type: "text", option: "" },
        { text: "", image: null, isCorrect: false, type: "text", option: "" },
      ],
      isOpen: true,
    },
  ]);

  useEffect(() => {
    console.log("Extracted quizId:", quiz_id);

    if (
      !quiz_id ||
      quiz_id === "null" ||
      quiz_id === "undefined" ||
      quiz_id.trim() === ""
    ) {
      toast.error("Please create a quiz first before adding questions");
      setTimeout(() => navigate("/add-quiz"), 100);
      return;
    }

    // Fetch existing quiz details
    fetchQuizDetails();
  }, [quiz_id, navigate]);

  // Fetch existing quiz details to check if there are questions to prefill
  const fetchQuizDetails = async () => {
    try {
      setIsLoading(true);
      const response = await getDetails(quiz_id);

      if (response.data && response.data.statusCode === 200) {
        // Store the quiz details in localStorage for reference
        localStorage.setItem(
          "current_quiz_details",
          JSON.stringify(response.data.result)
        );

        const quizData = response.data.result;

        // Check if quiz has questions (indicating edit mode)
        if (quizData.questions && quizData.questions.length > 0) {
          setIsEditMode(true);
          // Format existing questions to match our component state structure
          const formattedQuestions = quizData.questions.map((question) => {
            // Parse options from JSON string to array of objects
            let optionsArray = [];
            try {
              optionsArray = JSON.parse(question.options);
            } catch (e) {
              console.error("Error parsing options:", e);
              optionsArray = [];
            }

            return {
              question_id: question.question_id,
              question: question.question || "",
              questionImage: question.question_file
                ? question.question_file
                : null,
              question_file: question.question_file || "",
              options: optionsArray.map((opt) => ({
                text: opt.type === "text" ? opt.option : "",
                image: opt.type === "file" ? opt.option : null,
                isCorrect:
                  opt.option === question.answer &&
                  opt.type === question.answer_type,
                type: opt.type || "text",
                option: opt.option || "",
              })),
              isOpen: true,
            };
          });

          // Replace default empty question with fetched questions
          setQuestions(formattedQuestions);
          toast.success("Loaded existing questions for editing");
        } else {
          // No questions exist yet, keep default empty question
          console.log("No existing questions found, create mode activated");
        }
      }
    } catch (error) {
      console.error("Error fetching quiz details:", error);
      toast.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Upload a file and get back the URL
  const handleFileUpload = async (file) => {
    try {
      setIsLoading(true);
      const response = await uploadFile(file, "image");

      if (response.data && response.data.statusCode === 200) {
        return response.data.result.media_url;
      } else {
        throw new Error("Failed to upload file");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload the file. Please try again.");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate that at least one option per question is marked as correct
    const invalidQuestions = questions.filter(
      (q) => !q.options.some((opt) => opt.isCorrect)
    );

    if (invalidQuestions.length > 0) {
      toast.error(
        `Please mark a correct answer for question ${
          questions.indexOf(invalidQuestions[0]) + 1
        }`
      );
      return;
    }

    try {
      setIsLoading(true);

      // Format the data according to API requirements
      const formattedQuestions = questions?.map((question) => {
        // Find the correct option
        const correctOptionIndex = question.options.findIndex(
          (opt) => opt.isCorrect
        );
        const correctOption = question.options[correctOptionIndex];

        return {
          // Include question_id only if it exists (for editing)
          ...(question.question_id && {
            question_id: String(question.question_id),
          }),
          question: question.question,
          question_file: question.question_file,
          options: question.options.map((option) => ({
            type: option.image ? "file" : "text",
            option: option.image ? option.option : option.text,
          })),
          answer: {
            value: correctOption.image
              ? correctOption.option
              : correctOption.text,
            type: correctOption.image ? "file" : "text",
          },
        };
      });

      // Create the payload
      const payload = {
        quiz_id: String(quiz_id),
        deleted_questions: deletedQuestionIds.join(","),
        questions: formattedQuestions,
      };

      console.log("Submitting payload:", payload);

      // Call the API
      const response = await addQuestions(payload);

      if (response.data && response.data.statusCode === 200) {
        toast.success(
          response.data.statusMessage ||
            (isEditMode
              ? "Questions updated successfully!"
              : "Questions added successfully!")
        );
        navigate("/quiz-list");
      } else {
        toast.error(
          `Failed to ${
            isEditMode ? "update" : "add"
          } questions. Please try again.`
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "adding"} questions:`,
        error
      );
      toast.error(
        `An error occurred while ${
          isEditMode ? "updating" : "adding"
        } questions. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handles file upload for the Question Sheet (dummy for now)
  const handleQuestionSheetUpload = (e) => {
    const file = e.target.files[0];
    console.log("Question sheet uploaded:", file);
    toast.error("Bulk upload functionality is not implemented yet.");
  };

  // Handle question image upload
  const handleQuestionImageUpload = async (questionIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Create local preview immediately
      const updatedQuestions = [...questions];

      // Revoke previous object URL to free memory
      if (
        updatedQuestions[questionIndex].questionImage &&
        updatedQuestions[questionIndex].questionImage.startsWith("blob:")
      ) {
        URL.revokeObjectURL(updatedQuestions[questionIndex].questionImage);
      }

      // Set local preview
      updatedQuestions[questionIndex].questionImage = URL.createObjectURL(file);
      setQuestions(updatedQuestions);

      // Upload file to server
      const fileUrl = await handleFileUpload(file);

      if (fileUrl) {
        updatedQuestions[questionIndex].question_file = fileUrl;
        setQuestions([...updatedQuestions]);
      } else {
        // If upload failed, revert the preview
        updatedQuestions[questionIndex].questionImage = null;
        setQuestions([...updatedQuestions]);
      }
    } catch (error) {
      console.error("Error handling question image upload:", error);
      toast.error("Failed to upload the image. Please try again.");
    }
  };

  const handleOptionImageUpload = async (questionIndex, optionIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Create local preview immediately
      const updatedQuestions = [...questions];

      // Revoke previous object URL to free memory
      if (
        updatedQuestions[questionIndex].options[optionIndex].image &&
        updatedQuestions[questionIndex].options[optionIndex].image.startsWith(
          "blob:"
        )
      ) {
        URL.revokeObjectURL(
          updatedQuestions[questionIndex].options[optionIndex].image
        );
      }

      // Set local preview
      updatedQuestions[questionIndex].options[optionIndex].image =
        URL.createObjectURL(file);
      setQuestions(updatedQuestions);

      // Upload file to server
      const fileUrl = await handleFileUpload(file);

      if (fileUrl) {
        updatedQuestions[questionIndex].options[optionIndex].option = fileUrl;
        updatedQuestions[questionIndex].options[optionIndex].type = "file";
        setQuestions([...updatedQuestions]);
      } else {
        // If upload failed, revert the preview
        updatedQuestions[questionIndex].options[optionIndex].image = null;
        setQuestions([...updatedQuestions]);
      }
    } catch (error) {
      console.error("Error handling option image upload:", error);
      alert("Failed to upload the image. Please try again.");
    }
  };

  const removeQuestionImage = (questionIndex) => {
    const updatedQuestions = [...questions];

    // Revoke object URL before removing
    if (
      updatedQuestions[questionIndex].questionImage &&
      updatedQuestions[questionIndex].questionImage.startsWith("blob:")
    ) {
      URL.revokeObjectURL(updatedQuestions[questionIndex].questionImage);
    }

    updatedQuestions[questionIndex].questionImage = null;
    updatedQuestions[questionIndex].question_file = "";
    setQuestions(updatedQuestions);

    // Reset file input
    const fileInput = document.getElementById(`questionImage-${questionIndex}`);
    if (fileInput) fileInput.value = "";
  };

  const removeOptionImage = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];

    // Revoke object URL before removing
    if (
      updatedQuestions[questionIndex].options[optionIndex].image &&
      updatedQuestions[questionIndex].options[optionIndex].image.startsWith(
        "blob:"
      )
    ) {
      URL.revokeObjectURL(
        updatedQuestions[questionIndex].options[optionIndex].image
      );
    }

    updatedQuestions[questionIndex].options[optionIndex].image = null;
    updatedQuestions[questionIndex].options[optionIndex].type = "text";
    updatedQuestions[questionIndex].options[optionIndex].option =
      updatedQuestions[questionIndex].options[optionIndex].text;
    setQuestions(updatedQuestions);

    // Reset file input
    const fileInput = document.getElementById(
      `optionImage-${questionIndex}-${optionIndex}`
    );
    if (fileInput) fileInput.value = "";
  };

  // Toggle correct answer
  const toggleCorrectOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options = updatedQuestions[
      questionIndex
    ].options.map((option, idx) => ({
      ...option,
      isCorrect: idx === optionIndex,
    }));
    setQuestions(updatedQuestions);
  };

  // Add new question
  const addMoreQuestions = () => {
    setQuestions([
      ...questions,
      {
        question_id: "", // Empty for new questions
        question: "",
        questionImage: null,
        question_file: "",
        options: [
          { text: "", image: null, isCorrect: false, type: "text", option: "" },
          { text: "", image: null, isCorrect: false, type: "text", option: "" },
          { text: "", image: null, isCorrect: false, type: "text", option: "" },
          { text: "", image: null, isCorrect: false, type: "text", option: "" },
        ],
        isOpen: true,
      },
    ]);
  };

  // Delete a question
  const deleteQuestion = (index) => {
    const questionToDelete = questions[index];

    // If question has an ID (existing question), add to deleted list
    if (questionToDelete.question_id) {
      setDeletedQuestionIds([
        ...deletedQuestionIds,
        questionToDelete.question_id,
      ]);
    }

    // Remove question from the UI
    setQuestions(questions.filter((_, i) => i !== index));
  };

  // Update option text
  const updateOptionText = (questionIndex, optionIndex, text) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex].text = text;
    // Also update the option field for API compatibility
    if (updatedQuestions[questionIndex].options[optionIndex].type === "text") {
      updatedQuestions[questionIndex].options[optionIndex].option = text;
    }
    setQuestions(updatedQuestions);
  };

  return (
    <div>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="add-college">
          <div className="subheader mb-3">
            <div className="d-flex">
              <h2
                className="heading-36 me-4"
                style={{ cursor: "pointer", 
                 }}
                onClick={() => navigate(`/edit-quiz/${quiz_id}`)}
              >
                Edit Quiz
              </h2>
              <h2
                className="heading-36 " 
                style={{ cursor: "pointer", fontSize :"22px"
                }}
              >
                {isEditMode ? "Edit Questions" : "Add Questions"}
              </h2>
            </div>

            <div className="subheader-right-btm">
              <button
                type="button"
                className="btn-purple"
                // onClick={() => navigate("/quiz-list")}
                onClick={() =>
                  generateConfirm(
                    "Are you sure you want to Discard Changes ?",
                    "Press Yes if you want to discard",
                    "quiz-discard",
                    () => {
                      navigate("/quiz-list");
                    }
                  )
                }
                disabled={isLoading}
              >
                Discard
              </button>
              <button
                type="submit"
                className="btn-purple discard"
                disabled={isLoading || questions.length === 0 || !quiz_id}
              >
                {isLoading ? "Saving..." : isEditMode ? "Update" : "Save"}
              </button>
            </div>
          </div>

          <div className="addoverview-data">
          <div className="upload-question-sheet">
  <div className="upload-field">
    <label htmlFor="questionSheet" className="upload-label">
      <FontAwesomeIcon icon={faCamera} className="upload-icon" />
      Upload Sheet
    </label>
    <input
      type="file"
      id="questionSheet"
      accept=".xlsx,.xls,.csv"
      className="file-input"
      onChange={handleQuestionSheetUpload}
      disabled={isLoading}
    />
  </div>
  
  <div className="download-sample">
    <label className="download-label" 
    // onClick={"handleDownloadSample" }
    >
      <FontAwesomeIcon icon={faDownload} className="download-icon" />
      Download Sample
    </label>
  </div>
</div>
            <div>
              <h2>{isEditMode ? "Edit Questions" : "Add Questions"}</h2>
            </div>

            {questions.map((questionItem, questionIndex) => (
              <div key={questionIndex} className="question-section">
                {/* Question Header */}
                <div className="question-header">
                  <h3>Question {questionIndex + 1}</h3>
                  <div className="header-actions">
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="accordion-icon"
                      onClick={() => {
                        const updatedQuestions = [...questions];
                        updatedQuestions[questionIndex].isOpen =
                          !updatedQuestions[questionIndex].isOpen;
                        setQuestions(updatedQuestions);
                      }}
                    />
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="delete-icon"
                      onClick={() => deleteQuestion(questionIndex)}
                      style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
                    />
                  </div>
                </div>

                {questionItem.isOpen && (
                  <>
                    {/* Question Input */}
                    <div className="question-input-wrapper">
                      <input
                        type="text"
                        className="form-control question-input"
                        placeholder="Enter your question"
                        value={questionItem.question}
                        onChange={(e) => {
                          const updatedQuestions = [...questions];
                          updatedQuestions[questionIndex].question =
                            e.target.value;
                          setQuestions(updatedQuestions);
                        }}
                        required
                        disabled={isLoading}
                      />
                      <label
                        htmlFor={`questionImage-${questionIndex}`}
                        className="image-upload-icon"
                        style={{
                          cursor: isLoading ? "not-allowed" : "pointer",
                        }}
                      >
                        {questionItem.questionImage ? (
                          <div className="image-thumbnail">
                            <img
                              src={questionItem.questionImage}
                              alt="Uploaded"
                            />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => removeQuestionImage(questionIndex)}
                              disabled={isLoading}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ) : (
                          <FontAwesomeIcon icon={faImage} />
                        )}
                        <input
                          type="file"
                          id={`questionImage-${questionIndex}`}
                          accept="image/*"
                          className="d-none"
                          onChange={(e) =>
                            handleQuestionImageUpload(questionIndex, e)
                          }
                          disabled={isLoading}
                        />
                      </label>
                    </div>

                    {/* Options */}
                    {questionItem.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="option-row">
                        <input
                          type="text"
                          className="form-control option-input"
                          placeholder={`Option ${optionIndex + 1}`}
                          value={option.text}
                          onChange={(e) =>
                            updateOptionText(
                              questionIndex,
                              optionIndex,
                              e.target.value
                            )
                          }
                          required={!option.image}
                          disabled={isLoading}
                        />
                        <label
                          htmlFor={`optionImage-${questionIndex}-${optionIndex}`}
                          className="image-upload-icon"
                          style={{
                            cursor: isLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {option.image ? (
                            <div className="image-thumbnail">
                              <img src={option.image} alt="Option" />
                              <button
                                type="button"
                                className="remove-image-btn"
                                onClick={() =>
                                  removeOptionImage(questionIndex, optionIndex)
                                }
                                disabled={isLoading}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          ) : (
                            <FontAwesomeIcon icon={faImage} />
                          )}
                          <input
                            type="file"
                            id={`optionImage-${questionIndex}-${optionIndex}`}
                            accept="image/*"
                            className="d-none"
                            onChange={(e) =>
                              handleOptionImageUpload(
                                questionIndex,
                                optionIndex,
                                e
                              )
                            }
                            disabled={isLoading}
                          />
                        </label>
                        <button
                          type="button"
                          className={`btn-check-option ${
                            option.isCorrect ? "active" : ""
                          }`}
                          onClick={() =>
                            toggleCorrectOption(questionIndex, optionIndex)
                          }
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}

            {/* Error message if no questions */}
            {questions.length === 0 && (
              <div className="alert alert-warning">
                At least one question is required. Please add a question.
              </div>
            )}

            {/* Add More Questions */}
            <button
              type="button"
              className="btn btn-primary add-more-btn"
              onClick={addMoreQuestions}
              disabled={isLoading}
            >
              Add more
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddQuestion;
